import React, { useState, useRef, useEffect } from 'react';
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Card, CardContent } from "../components/ui/card";
import { ChevronDown, ChevronUp, Mic, Image as ImageIcon, Video, Plus, Trash2, Sparkles } from 'lucide-react';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { useLocation } from 'react-router-dom';

const ItemTypes = {
  TOPIC: 'topic',
};

function DraggableTopic({ topic, index, moveTopic, addNewCanvas }) {
  const ref = useRef(null);
  const [, drop] = useDrop({
    accept: ItemTypes.TOPIC,
    hover(item) {
      if (!ref.current) {
        return;
      }
      const dragIndex = item.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) {
        return;
      }
      moveTopic(dragIndex, hoverIndex);
      item.index = hoverIndex;
    },
  });
  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.TOPIC,
    item: { type: ItemTypes.TOPIC, index },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drag(drop(ref));
  return (
    <li ref={ref} className={`mb-1 ${isDragging ? 'opacity-50' : ''}`}>
      <div className="flex items-center justify-between w-full">
        <Button
          variant="ghost"
          className="w-full justify-start text-left py-2 px-4 text-gray-700 hover:bg-gray-100 rounded-lg"
          onClick={() => addNewCanvas(topic)} // Create a new canvas with the selected topic
        >
          <span className="text-left">{topic}</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="text-gray-500 hover:text-gray-700"
          onClick={() => addNewCanvas(topic)}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </div>
    </li>
  );
}

function CanvasPage({ onPublish }) {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const topicFromQuery = query.get('topic');
  const typeFromQuery = query.get('type');

  // Set active tab based on type
  const initialTab = typeFromQuery === 'anecdote' ? 'Anecdotes' : 'Lifestory';
  const [activeTab, setActiveTab] = useState(initialTab);
  const [anecdoteCanvases, setAnecdoteCanvases] = useState([]);
  const [lifestoryCanvases, setLifestoryCanvases] = useState([]);
  const [activeCanvasId, setActiveCanvasId] = useState(null);
  const [expandedSections, setExpandedSections] = useState([]); // Declare expandedSections state

  // Declare refs for file inputs
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);

  // Define lifeStoryTopics
  const lifeStoryTopics = [
    "My First Memory",
    "The Day I Met My Best Friend",
    "My Favorite Childhood Toy",
    "The Weirdest Food I've Ever Eaten",
    "My Most Embarrassing Moment",
    "The Time I Felt Like a Superhero",
    "My Biggest 'Oops' Moment",
    "The Day I Conquered My Fear",
    "My Secret Talent",
    "The Funniest Joke I Know",
    "My Dream Vacation Spot",
    "The Strangest Dream I've Had",
    "My Favorite Family Tradition",
    "The Time I Got Lost (and Found)",
    "My First Crush",
    "The Coolest Thing I've Ever Made",
    "My Favorite Hiding Spot as a Kid",
    "The Time I Felt Most Proud",
    "My Biggest Kitchen Disaster",
    "The Wildest Adventure I've Been On",
    "My Favorite Rainy Day Activity",
    "The Time I Laughed So Hard I Cried",
    "My Most Prized Possession",
    "The Weirdest Job I've Ever Had",
    "My Favorite Childhood Game"
  ];

  const lifeSections = [
    { title: "Childhood Shenanigans", subsections: ["Playground Adventures", "Family Vacations", "School Days", "Imaginary Friends"] },
    { title: "Teenage Rollercoaster", subsections: ["First Crush", "Awkward Phases", "Friend Drama", "Discovering Passions"] },
    { title: "Adulting 101", subsections: ["College Chaos", "First Job Jitters", "Cooking Disasters", "Budgeting Blunders"] },
    { title: "Love & Relationships", subsections: ["Meet-Cute Stories", "Date Night Disasters", "Romantic Gestures", "Heartbreak & Healing"] },
    { title: "Career Chronicles", subsections: ["Dream Job Journey", "Office Pranks", "Work Fails", "Success Stories"] },
    { title: "Family Matters", subsections: ["Sibling Rivalries", "Parenting Adventures", "Pet Tales", "Holiday Hijinks"] },
    { title: "Travel Tales", subsections: ["Lost in Translation", "Food Adventures", "Cultural Shocks", "Hidden Gems"] },
    { title: "Personal Growth", subsections: ["Aha Moments", "Overcoming Fears", "Learning New Skills", "Self-Care Victories"] },
    { title: "Bucket List", subsections: ["Wild Dreams", "Personal Challenges", "Giving Back", "Legacy Ideas"] },
    { title: "Life's Playlist", subsections: ["Songs That Define Me", "Concert Memories", "Karaoke Disasters", "Music Milestones"] },
  ];

  // Load content from local storage on mount
  useEffect(() => {
    const savedAnecdotes = JSON.parse(localStorage.getItem('anecdoteCanvases')) || [];
    const savedLifestories = JSON.parse(localStorage.getItem('lifestoryCanvases')) || [];
    setAnecdoteCanvases(savedAnecdotes);
    setLifestoryCanvases(savedLifestories);

    // Set the active canvas to the first one if available
    if (savedAnecdotes.length > 0) {
      setActiveCanvasId(savedAnecdotes[0].id);
    } else if (savedLifestories.length > 0) {
      setActiveCanvasId(savedLifestories[0].id);
    }
  }, []);

  // Save content to local storage whenever it changes
  const handleSave = () => {
    localStorage.setItem('anecdoteCanvases', JSON.stringify(anecdoteCanvases));
    localStorage.setItem('lifestoryCanvases', JSON.stringify(lifestoryCanvases));
    alert("Progress saved!");
  };

  const toggleSection = (section) => {
    setExpandedSections(prev =>
      prev.includes(section)
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const addNewCanvas = (topic) => {
    const title = prompt("Enter a title for the new canvas:", topic);
    if (title) {
      const newCanvas = { id: Date.now(), topic: title, entry: '', type: activeTab }; // Add type here
      if (activeTab === 'Anecdotes') {
        setAnecdoteCanvases(prev => [newCanvas, ...prev]);
      } else {
        setLifestoryCanvases(prev => [newCanvas, ...prev]);
      }
      setActiveCanvasId(newCanvas.id);
    }
  };

  const deleteCanvas = (id) => {
    if (activeTab === 'Anecdotes') {
      setAnecdoteCanvases(prev => {
        const updatedCanvases = prev.filter(canvas => canvas.id !== id);
        if (activeCanvasId === id) {
          setActiveCanvasId(updatedCanvases[0]?.id || null);
        }
        return updatedCanvases;
      });
    } else {
      setLifestoryCanvases(prev => {
        const updatedCanvases = prev.filter(canvas => canvas.id !== id);
        if (activeCanvasId === id) {
          setActiveCanvasId(updatedCanvases[0]?.id || null);
        }
        return updatedCanvases;
      });
    }
  };

  const updateCanvasEntry = (id, entry) => {
    if (activeTab === 'Anecdotes') {
      setAnecdoteCanvases(prev =>
        prev.map(canvas =>
          canvas.id === id ? { ...canvas, entry } : canvas
        )
      );
    } else {
      setLifestoryCanvases(prev =>
        prev.map(canvas =>
          canvas.id === id ? { ...canvas, entry } : canvas
        )
      );
    }
  };

  const handleImageUpload = () => {
    fileInputRef.current?.click();
  };

  const handleVideoUpload = () => {
    videoInputRef.current?.click();
  };

  const handleVoiceRecording = () => {
    console.log("Voice recording toggled");
  };

  const handlePublish = () => {
    const activeCanvas = activeTab === 'Anecdotes' 
      ? anecdoteCanvases.find(canvas => canvas.id === activeCanvasId) 
      : lifestoryCanvases.find(canvas => canvas.id === activeCanvasId);

    if (activeCanvas) {
      console.log("Publishing:", activeCanvas); // Log the canvas being published
      onPublish(activeCanvas); // Call the onPublish function with the active canvas
    } else {
      console.error("No active canvas found to publish.");
    }
  };

  return (
    <div>
      {/* Tabs for Anecdotes and Lifestory */}
      <div className="flex justify-between items-center mb-4">
        <div className="flex space-x-4">
          <Button
            className={`px-4 py-2 rounded-lg ${activeTab === 'Anecdotes' ? 'bg-[#52A384] text-white' : 'bg-white text-gray-700 border border-black-600'}`}
            onClick={() => setActiveTab('Anecdotes')}
          >
            Anecdotes
          </Button>
          <Button
            className={`px-4 py-2 rounded-lg ${activeTab === 'Lifestory' ? 'bg-[#52A384] text-white' : 'bg-white text-gray-700 border border-black-600'}`}
            onClick={() => setActiveTab('Lifestory')}
          >
            Lifestory
          </Button>
        </div>
        <Button
          className="px-4 py-2 bg-blue-500 text-white rounded-lg"
          onClick={handleSave}
        >
          Save
        </Button>
      </div>

      <div className="flex h-full">
        {/* Left Sidebar */}
        <div className="w-1/4 bg-white border-r border-gray-300 overflow-hidden flex flex-col rounded-3xl shadow-lg">
          <div className="p-4 border-b border-gray-300 flex justify-between items-center">
            <h2 className="text-xl font-bold">Anecdotes</h2>
          </div>
          <ScrollArea className="flex-grow">
            <ul className="p-2 space-y-2">
              {lifeStoryTopics.map((topic, index) => (
                <DraggableTopic
                  key={topic}
                  topic={topic}
                  index={index}
                  moveTopic={() => {}} // Implement drag-and-drop logic if needed
                  addNewCanvas={activeTab === 'Anecdotes' ? addNewCanvas : () => {}} // Only allow adding in Anecdotes tab
                />
              ))}
            </ul>
          </ScrollArea>
        </div>

        {/* Main Content */}
        <div className="flex-grow p-8 bg-[#52A384] rounded-3xl mx-4">
          <div className="grid grid-cols-1 gap-4">
            {(activeTab === 'Anecdotes' ? anecdoteCanvases : lifestoryCanvases).map((canvas) => (
              <div key={canvas.id} className="mb-4">
                {activeCanvasId === canvas.id ? (
                  <Card className="bg-white rounded-1xl shadow-lg overflow-hidden mb-4">
                    <CardContent className="p-6 flex flex-col">
                      <div className="flex justify-between items-center mb-4">
                        <h2 className="text-xl font-semibold text-gray-800">{canvas.topic}</h2>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-gray-500 hover:text-gray-700"
                          onClick={() => deleteCanvas(canvas.id)}
                        >
                          <Trash2 className="h-5 w-5" />
                        </Button>
                      </div>
                      <Textarea
                        placeholder="Start writing your story..."
                        className="flex-grow text-base focus:ring-0 resize-none p-0 mb-4 border-none"
                        value={canvas.entry}
                        onChange={(e) => updateCanvasEntry(canvas.id, e.target.value)} // Update content for the active canvas
                      />
                      <div className="flex justify-between items-center">
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-[#82C7A5] text-blue-700 rounded-full px-3 py-2 hover:bg-[#52A384] transition duration-200"
                        >
                          Generate AI video
                          <Sparkles className="h-4 w-4 ml-2 text-blue-700" />
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-[#000000] text-white rounded-full px-3 py-2"
                          onClick={handlePublish} // Call publish function
                        >
                          Publish
                        </Button>
                        <div className="flex space-x-4">
                          <Button variant="ghost" size="sm" className="text-black hover:text-gray-700" onClick={handleImageUpload}>
                            <ImageIcon className="h-5 w-5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-black hover:text-gray-700" onClick={handleVoiceRecording}>
                            <Mic className="h-5 w-5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="text-black hover:text-gray-700" onClick={handleVideoUpload}>
                            <Video className="h-5 w-5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ) : (
                  <Button
                    variant="ghost"
                    className="w-full text-left py-2 px-4 bg-white rounded-lg shadow-md hover:bg-gray-100"
                    onClick={() => setActiveCanvasId(canvas.id)}
                  >
                    {canvas.topic}
                  </Button>
                )}
              </div>
            ))}
            <div className="flex justify-center mt-0">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => addNewCanvas("New Anecdote")}
                className="bg-black text-white rounded-full w-8 h-8 flex items-center justify-center shadow-lg hover:bg-gray-800"
              >
                <Plus className="h-6 w-6" />
              </Button>
            </div>
          </div>
        </div>

        {/* Right Sidebar */}
        <div className="w-1/4 bg-white border-l border-gray-300 overflow-hidden flex flex-col rounded-3xl shadow-lg">
          <div className="p-4 border-b border-gray-300 flex justify-between items-center">
            <h2 className="text-xl font-bold">Lifestory</h2>
          </div>
          <ScrollArea className="flex-grow">
            <ul className="p-2 space-y-2">
              {lifeSections.map((section) => (
                <li key={section.title} className="border-b border-gray-200 pb-2">
                  <Button
                    variant="ghost"
                    className="w-full justify-between py-2 text-left hover:bg-gray-100 rounded-md"
                    onClick={() => toggleSection(section.title)}
                  >
                    {section.title}
                    {expandedSections.includes(section.title) ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </Button>
                  {expandedSections.includes(section.title) && section.subsections.length > 0 && (
                    <ul className="mt-1 pl-4 space-y-1">
                      {section.subsections.map((subsection) => (
                        <li key={subsection} className="flex justify-between items-center">
                          <Button variant="link" className="text-sm text-[#52A384] hover:text-green-800 p-0">{subsection}</Button>
                          <Button
                            variant="outline"
                            size="icon"
                            onClick={() => addNewCanvas(subsection)} // Add canvas for the selected subsection
                            className="bg-black text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg hover:bg-gray-800"
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          </ScrollArea>
        </div>

        {/* Hidden file inputs */}
        <input
          type="file"
          ref={fileInputRef}
          className="hidden"
          accept="image/*"
          onChange={(e) => console.log("Image file selected:", e.target.files?.[0])}
        />
        <input
          type="file"
          ref={videoInputRef}
          className="hidden"
          accept="video/*"
          onChange={(e) => console.log("Video file selected:", e.target.files?.[0])}
        />
      </div>
    </div>
  );
}

export default function App() {
  const [publishedStories, setPublishedStories] = useState([]); // State to hold published stories
  const [publishedCanvas, setPublishedCanvas] = useState([]); // State to hold published canvas entries

  const handlePublish = (canvas) => {
    console.log("Publishing canvas:", canvas); // Log the canvas being published
    if (canvas) {
      if (canvas.type === 'Anecdotes') {
        setPublishedStories(prev => [...prev, { topic: canvas.topic, entry: canvas.entry }]);
      } else if (canvas.type === 'Lifestory') {
        setPublishedCanvas(prev => [...prev, { topic: canvas.topic, entry: canvas.entry }]);
      }
    }
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <CanvasPage onPublish={handlePublish} />
      {/* Render the profile page or stories section here */}
      <div>
        <h2>Published Stories</h2>
        <ul>
          {publishedStories.map((story, index) => (
            <li key={index}>{story.topic}: {story.entry}</li>
          ))}
        </ul>
        <h2>Your Life Canvas Entries</h2>
        <ul>
          {publishedCanvas.map((canvas, index) => (
            <li key={index}>{canvas.topic}: {canvas.entry}</li>
          ))}
        </ul>
      </div>
    </DndProvider>
  );
}